/* KOYO-CUSTOM: ada-dialog-a11y — modal semantics + focus management for the cart drawer and the
 * newsletter popup (WCAG 2.4.3 Focus Order, 2.1.2 No Keyboard Trap, 4.1.2 Name/Role/Value).
 *
 * Why this exists (SR run 2026-08-18, findings F-2026-08-18-tc-05 / -newsletter-popup):
 *   - #cart shipped with no dialog role, no accessible name and no focus handling that survived Xtra's
 *     drawer re-render. VoiceOver never announced a dialog; five Tabs walked background product links;
 *     Escape closed the drawer but left focus nowhere.
 *   - The delayed newsletter popup does get role="dialog" from plugin-popups.js, but its
 *     aria-labelledby/aria-describedby point at "<data-title>-tab" — an id that only exists when a
 *     trigger link opened the popup. Auto-opened, it was an unnamed dialog with focus left on <body>.
 *
 * Design notes:
 *   - Role/aria-modal/aria-labelledby for #cart are in sections/side-cart.liquid so the semantics are
 *     visible in markup; this file only owns behaviour plus repairing what plugin-popups.js sets at runtime.
 *   - A dialog without containment is a modal lie, which is why the earlier cart work deliberately stayed
 *     announce-only. The trap below is what makes the dialog claim honest; the add-to-cart status message
 *     stays in assets/koyo-a11y-cart.js and never moves focus.
 *   - Background content is hidden from AT by aria-modal rather than the `inert` attribute: #root carries
 *     Xtra's own panel state classes and marking it inert breaks the drawer's own transitions.
 */
(function () {
  'use strict';

  if (window.__koyoA11yDialog) return; // idempotent: never install the observers twice
  window.__koyoA11yDialog = true;

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  // selector: matched once at install; the elements themselves are long-lived (layout / section markup).
  var REGISTRY = [
    { key: 'cart', selector: 'aside#cart.m6pn', labelId: 'koyo-cart-drawer-title', closeSelector: '.m6pn-close' },
    { key: 'newsletter', selector: '[data-title="newsletter-popup"]', labelId: 'newsletter-popup-title', closeSelector: 'a.close' },
  ];

  var entries = [];
  var openStack = []; // most recently opened dialog last — the newsletter popup can open over the drawer
  var lastActivated = null;

  var CART_STATUS_ID = 'koyo-cart-drawer-status';
  var CART_ADD_PENDING_WINDOW_MS = 2000; // how long after an add response a drawer open counts as "from that add"
  var CART_MESSAGE_WAIT_MS = 1200; // give up waiting for the confirmation and focus anyway
  var CART_STATUS_DELAY_MS = 1000; // let the dialog-entry phrase finish before the independent polite update
  var cartAddPendingAt = 0;
  var CART_MESSAGE_TTL_MS = 8000; // an add confirmation older than this belongs to a previous interaction
  var cartMessage = null; // { text, at } published by assets/koyo-a11y-cart.js

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (el.closest('[aria-hidden="true"]')) return false;
    var rect = el.getBoundingClientRect();
    if (!rect.width && !rect.height) return false;
    var style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  function focusables(root) {
    var list = [];
    var found = root.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < found.length; i++) if (isVisible(found[i])) list.push(found[i]);
    return list;
  }

  // Open state is expressed the same way by both widgets: Xtra's openPanel/hidePanels and
  // plugin-popups.js showPopupForElement/hidePopupForElement both flip aria-hidden.
  function isOpen(el) {
    return !!el && el.getAttribute('aria-hidden') === 'false';
  }

  /**
   * The dialog keyboard input belongs to: the one opened most recently and still open. Registry order
   * cannot decide this — the delayed newsletter popup can appear on top of an already-open cart drawer,
   * and containment must follow what is actually in front.
   */
  function openEntry() {
    for (var i = openStack.length - 1; i >= 0; i--) if (isOpen(openStack[i].el)) return openStack[i];
    for (var j = entries.length - 1; j >= 0; j--) if (isOpen(entries[j].el)) return entries[j];
    return null;
  }

  function pushOpen(entry) {
    dropOpen(entry);
    openStack.push(entry);
  }

  function dropOpen(entry) {
    var idx = openStack.indexOf(entry);
    if (idx !== -1) openStack.splice(idx, 1);
  }

  function insideAnyDialog(el) {
    for (var i = 0; i < entries.length; i++) if (entries[i].el.contains(el)) return true;
    return false;
  }

  /** Remember what the user activated, so a close can put focus back where it came from. */
  function rememberTrigger(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var el = target.closest('a,button,[role="button"],input[type="submit"],input[type="button"]');
    if (el && !insideAnyDialog(el)) lastActivated = el;
  }

  document.addEventListener('click', rememberTrigger, true); // VoiceOver's VO+Space dispatches a real click
  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') rememberTrigger(e);
    },
    true,
  );

  /**
   * Repair the runtime-assigned dialog attributes. plugin-popups.js rewrites these on every init, so this
   * runs again on each open rather than once at install.
   */
  function ensureSemantics(entry) {
    var el = entry.el;
    if (el.getAttribute('role') !== 'dialog') el.setAttribute('role', 'dialog');
    if (el.getAttribute('aria-modal') !== 'true') el.setAttribute('aria-modal', 'true');

    var labelled = el.getAttribute('aria-labelledby');
    if (!labelled || !document.getElementById(labelled)) {
      if (document.getElementById(entry.labelId)) el.setAttribute('aria-labelledby', entry.labelId);
      else el.removeAttribute('aria-labelledby');
    }
    var described = el.getAttribute('aria-describedby');
    if (described && !document.getElementById(described)) el.removeAttribute('aria-describedby');
  }

  function freshCartMessage() {
    if (!cartMessage) return null;
    return Date.now() - cartMessage.at <= CART_MESSAGE_TTL_MS ? cartMessage.text : null;
  }

  /**
   * Put the visible add confirmation inside the drawer.
   *
   * The polite region in the layout sits outside #root, so once the drawer claims aria-modal the region's
   * update is in the part of the page assistive tech is told to ignore — the 2026-08-18 retest recorded a
   * correct DOM status and an empty six-second VoiceOver capture. The next re-test then showed that
   * VoiceOver/Chrome also omitted a visually-hidden aria-describedby value when focus entered the
   * drawer. The confirmation is therefore visible beside the Cart heading and remains in normal dialog
   * reading order, while retaining polite status semantics. Xtra re-renders the drawer on every add, so
   * this is re-applied from the settle timers.
   */
  function ensureDrawerStatus(entry, text) {
    var node = entry.el.querySelector('#' + CART_STATUS_ID);
    var heading = document.getElementById(entry.labelId);
    if (!node) {
      node = document.createElement('span');
      node.id = CART_STATUS_ID;
      node.className = 'koyo-cart-drawer-status';
    }
    // A live region INSIDE the modal: the layout region outside it is pruned while aria-modal is set.
    // Deliberately not focusable. Focus remains on the visible heading; this visible text can also be
    // reached in normal screen-reader reading order when automatic status/description speech is absent.
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');
    if (heading && node.parentElement !== heading) heading.appendChild(node);
    else if (!node.isConnected) entry.el.appendChild(node);
    if (typeof text === 'string' && node.textContent !== text) node.textContent = text;
    // The describedby path was removed after the valid 21:57 and 22:08 VoiceOver runs: even with the
    // populated visible status present before h2 focus, Chrome/VoiceOver omitted that description. Keep
    // the heading's dialog-entry speech and the later live-region update as independent channels.
    if (heading && heading.getAttribute('aria-describedby') === CART_STATUS_ID) {
      heading.removeAttribute('aria-describedby');
    }
    if (entry.el.getAttribute('aria-describedby') === CART_STATUS_ID) {
      entry.el.removeAttribute('aria-describedby');
    }
    return node;
  }

  /**
   * Announce inside the open drawer.
   *
   * cart-add-ready has already put the expected confirmation in the heading before dialog entry. Replace
   * it with the authoritative count after Xtra updates #cart-count; the status remains non-focusable.
   */
  function announceInDrawer(entry, message) {
    ensureDrawerStatus(entry, freshCartMessage());
    entry.awaitingMessage = false;
    if (entry.messageWait) {
      clearTimeout(entry.messageWait);
      entry.messageWait = null;
    }
    if (entry.statusTimer) clearTimeout(entry.statusTimer);
    entry.statusPending = true;
    entry.statusTimer = setTimeout(function () {
      entry.statusTimer = null;
      entry.statusPending = false;
      if (!isOpen(entry.el)) return;
      ensureDrawerStatus(entry, message);
    }, CART_STATUS_DELAY_MS);
  }

  function clearDrawerStatus(entry) {
    var node = entry.el.querySelector('#' + CART_STATUS_ID);
    // Keep the stable empty region mounted for the next add; only stale text and relationships go away.
    if (node) node.textContent = '';
    var heading = document.getElementById(entry.labelId);
    if (heading && heading.getAttribute('aria-describedby') === CART_STATUS_ID) {
      heading.removeAttribute('aria-describedby');
    }
    if (entry.el.getAttribute('aria-describedby') === CART_STATUS_ID) {
      entry.el.removeAttribute('aria-describedby');
    }
  }

  /** Move focus to the dialog heading (announces the dialog name), else its first visible control. */
  function focusInto(entry) {
    var heading = document.getElementById(entry.labelId);
    if (heading && entry.el.contains(heading) && isVisible(heading)) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus();
      if (entry.el.contains(document.activeElement)) return;
    }
    var list = focusables(entry.el);
    if (list.length) {
      list[0].focus();
      return;
    }
    if (!entry.el.hasAttribute('tabindex')) entry.el.setAttribute('tabindex', '-1');
    entry.el.focus();
  }

  function clearSettle(entry) {
    for (var i = 0; i < entry.settle.length; i++) clearTimeout(entry.settle[i]);
    entry.settle = [];
  }

  function onOpen(entry) {
    pushOpen(entry);
    ensureSemantics(entry);
    if (entry.key === 'cart') {
      ensureDrawerStatus(entry, freshCartMessage());
      // Opened by an add whose confirmation is still being composed: hold the focus move so the phrase
      // VoiceOver builds on entry already contains it. announceInDrawer does the focusing instead.
      entry.awaitingMessage = Date.now() - cartAddPendingAt < CART_ADD_PENDING_WINDOW_MS && !freshCartMessage();
      if (entry.awaitingMessage) {
        entry.messageWait = setTimeout(function () {
          entry.awaitingMessage = false;
          // Never claim focus from a dialog that opened on top in the meantime.
          if (openEntry() !== entry) return;
          if (isOpen(entry.el) && !entry.el.contains(document.activeElement)) focusInto(entry);
        }, CART_MESSAGE_WAIT_MS); // the confirmation never came — do not strand focus outside the dialog
      }
    }
    if (lastActivated && lastActivated.isConnected && !entry.el.contains(lastActivated)) {
      entry.trigger = lastActivated;
    }
    if (!entry.awaitingMessage) focusInto(entry);
    // Xtra re-renders the drawer contents after an add, which drops the element we just focused.
    // Re-assert while the panel settles, but only when focus actually fell out of the dialog.
    clearSettle(entry);
    [150, 450, 900].forEach(function (delay) {
      entry.settle.push(
        setTimeout(function () {
          if (!isOpen(entry.el)) return;
          // Xtra's re-render drops the node; put it back, empty until there is a message to carry.
          if (entry.key === 'cart') {
            ensureDrawerStatus(entry, entry.statusPending ? null : freshCartMessage());
          }
          if (entry.awaitingMessage) return; // announceInDrawer owns the first focus in this case
          if (!entry.el.contains(document.activeElement)) focusInto(entry);
        }, delay),
      );
    });
  }

  /** The exact-count confirmation is prepared before open, then reconciled after Xtra updates the count. */
  document.addEventListener('koyo:cart-add-pending', function () {
    cartAddPendingAt = Date.now();
  });

  // This fires from the fetch observer before Xtra handles the successful response and opens the
  // drawer. Pre-populating the visible status means the very first focus on the Cart heading includes
  // the confirmation; changing a name after focus proved silent in the 22:27 VoiceOver run.
  document.addEventListener('koyo:cart-add-ready', function (e) {
    var text = (e && e.detail && e.detail.message) || '';
    if (!text) return;
    cartMessage = { text: text, at: Date.now() };
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].key === 'cart') ensureDrawerStatus(entries[i], text);
    }
  });

  document.addEventListener('koyo:cart-added', function (e) {
    var text = (e && e.detail && e.detail.message) || '';
    if (!text) return;
    cartMessage = { text: text, at: Date.now() };

    var entry = null;
    for (var i = 0; i < entries.length; i++) if (entries[i].key === 'cart') entry = entries[i];
    if (!entry || !isOpen(entry.el) || openEntry() !== entry) return;

    announceInDrawer(entry, text);
  });

  function onClose(entry) {
    clearSettle(entry);
    dropOpen(entry);
    if (entry.key === 'cart') {
      clearDrawerStatus(entry); // a stale confirmation must not be read on the next open
      cartMessage = null;
      cartAddPendingAt = 0;
      entry.awaitingMessage = false;
      entry.statusPending = false;
      if (entry.messageWait) {
        clearTimeout(entry.messageWait);
        entry.messageWait = null;
      }
      if (entry.statusTimer) {
        clearTimeout(entry.statusTimer);
        entry.statusTimer = null;
      }
    }
    // A dialog that closed on top of another one hands focus back to the dialog underneath, not to its
    // own trigger — otherwise dismissing the popup would drop the user out of the open cart drawer.
    var below = openEntry();
    if (below) {
      entry.trigger = null;
      focusInto(below);
      return;
    }
    var trigger = entry.trigger;
    entry.trigger = null;
    if (!trigger || !trigger.isConnected || !isVisible(trigger)) return;
    // Xtra's hidePanels also restores focus (to the header cart link, after 100ms) but only when
    // whatInput reports a keyboard interaction — which VoiceOver activation does not produce. Land after
    // it so the trigger the user actually activated wins deterministically.
    setTimeout(function () {
      if (openEntry()) return; // another dialog took over in the meantime
      try {
        trigger.focus();
      } catch (err) {
        /* element became unfocusable between close and restore */
      }
    }, 200);
  }

  // --- keyboard: containment + Escape ---------------------------------------------------------------

  document.addEventListener(
    'keydown',
    function (e) {
      var entry = openEntry();
      if (!entry) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        var closers = entry.el.querySelectorAll(entry.closeSelector);
        for (var i = 0; i < closers.length; i++) {
          if (isVisible(closers[i])) {
            e.preventDefault();
            closers[i].click();
            return;
          }
        }
        return;
      }

      if (e.key !== 'Tab') return;
      var list = focusables(entry.el);
      if (!list.length) {
        e.preventDefault();
        focusInto(entry);
        return;
      }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (!entry.el.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    true,
  );

  // --- install ---------------------------------------------------------------------------------------

  function install() {
    REGISTRY.forEach(function (config) {
      var el = document.querySelector(config.selector);
      if (!el) return;
      var entry = {
        el: el,
        key: config.key,
        labelId: config.labelId,
        closeSelector: config.closeSelector,
        trigger: null,
        settle: [],
        statusPending: false,
        statusTimer: null,
        wasOpen: isOpen(el),
      };
      entries.push(entry);
      ensureSemantics(entry);

      new MutationObserver(function () {
        var open = isOpen(entry.el);
        if (open === entry.wasOpen) return;
        entry.wasOpen = open;
        if (open) onOpen(entry);
        else onClose(entry);
      }).observe(entry.el, { attributes: true, attributeFilter: ['aria-hidden', 'class'] });

      if (entry.wasOpen) onOpen(entry);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install);
  else install();
})();
